import { MouseEvent, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
    VisibilityIcon,
    VisibilityOffIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import CopyButton from "components/DesignComponents/CopyButton";
import { TooltipGeneral } from "components/DesignComponents/Tooltips/TooltipGeneral";
import { TOOLTIP_PLACEMENT } from "components/DesignComponents/Tooltips/types/enums";
import TableContext from "context/table/tableContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { usePermissions } from "shared/hooks/usePermissions";
import { ENDPOINT_PATH_SEGMENTS, ENDPOINTS } from "shared/types/enums";
import { RectangleLoader } from "shared";

const Password = () => {
    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const isMounted = useIsMounted();

    const {
        isSecretsReadDeviceNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const [isLoading, setIsLoading] = useState(true);
    const [value, setValue] = useState("");
    const [isError, setIsError] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const getSecrets = async () => {
            try {
                setIsLoading(true);

                const { data } = await getData(
                    `${ENDPOINTS.Devices}/${detailsData.imei}${ENDPOINT_PATH_SEGMENTS.Secrets}`
                );

                if (isMounted) {
                    if (data.bluetooth_password) {
                        setValue(data.bluetooth_password);
                        setIsError(false);
                    } else {
                        setValue("");
                        setIsError(true);
                    }
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    setValue("");
                    setIsError(true);
                    handleResponse(error);
                }
            }

            setIsLoading(false);
        };

        if (isSecretsReadDeviceNotAllowed) {
            setIsLoading(false);
            setValue("");
            setIsError(false);
            setIsVisible(false);
        } else {
            getSecrets();
        }

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSecretsReadDeviceNotAllowed]);

    const togglePswVisibility = (e: MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        setIsVisible(prev => !prev);
    };

    const visibilityIconTooltip = () => {
        if (isSecretsReadDeviceNotAllowed) {
            return getNoPermissionTooltipTitle(PERMISSIONS.DeviceSecretsRead);
        }

        return isVisible
            ? t("Security##password##hide password")
            : t("Security##password##show password");
    };

    const renderValue = () => {
        if (isLoading) {
            return (
                <RectangleLoader width="100%" height={20} borderRadius="46px" />
            );
        }

        if (isError) return "-";

        return (
            <>
                <span>{isVisible ? value : "*".repeat(9)}</span>

                <TooltipGeneral
                    title={visibilityIconTooltip()}
                    placement={TOOLTIP_PLACEMENT.Top}
                >
                    <span>
                        <Button
                            variant={BUTTON_VARIANTS.IconOnly}
                            size={BUTTON_SIZES.Tiny}
                            color={BUTTON_COLORS.White}
                            icon={
                                isVisible ? (
                                    <VisibilityOffIcon />
                                ) : (
                                    <VisibilityIcon />
                                )
                            }
                            onClick={togglePswVisibility}
                            disabled={isSecretsReadDeviceNotAllowed}
                        />
                    </span>
                </TooltipGeneral>

                <CopyButton
                    size={BUTTON_SIZES.Tiny}
                    copiedValue={value}
                    isDisabled={!isVisible}
                    disabledTooltipValue={t(
                        "Table details##bluetooth password##disabled copy tooltip"
                    )}
                />
            </>
        );
    };

    return <span>{renderValue()}</span>;
};

export default Password;
